import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "../../SVG/ChevronDown";
import "./tags.scss";

export interface TagOption {
  label: string;
  value: string;
  backgroundColor?: string;
  textColor?: string;
  showBorder?: boolean;
}

export interface TagsProps {
  label: string;
  backgroundColor?: string;
  textColor?: string;
  showBorder?: boolean;
  options?: TagOption[];
  onSelect?: (
    option: TagOption,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void;
  onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  shouldUsePortal?: boolean;
  /** Show the vertical divider between the label and the dropdown chevron. Defaults to false. */
  showSeparator?: boolean;
  className?: string;
  /** Whether to truncate the label text. */
  shouldTruncate?: boolean;
  /** Whether to show the search input in the dropdown. Defaults to true. */
  showSearch?: boolean;
}

export const Tags: React.FC<TagsProps> = ({
  label,
  backgroundColor,
  textColor,
  showBorder = false,
  options = [],
  onSelect,
  onClick,
  shouldUsePortal = false,
  showSeparator = false,
  className = "",
  shouldTruncate = false,
  showSearch = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<TagOption | null>(null);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const [searchQuery, setSearchQuery] = useState("");
  const tagRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const activeLabel = selectedOption?.label ?? label;
  const activeBg = selectedOption?.backgroundColor ?? backgroundColor;
  const activeText = selectedOption?.textColor ?? textColor;
  const activeBorder = selectedOption?.showBorder ?? showBorder;

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(normalizedQuery) ||
        option.value.toLowerCase().includes(normalizedQuery)
    );
  }, [options, searchQuery]);

  const updatePosition = useCallback(() => {
    if (!tagRef.current || !dropdownRef.current) return;
    const tagRect = tagRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const gap = 4;
    const spaceBelow = window.innerHeight - tagRect.bottom - gap;
    const direction = spaceBelow < dropdownHeight ? "up" : "down";
    setDropDirection(direction);

    if (shouldUsePortal) {
      const top =
        direction === "down"
          ? tagRect.bottom + gap
          : tagRect.top - gap - dropdownHeight;

      setPortalStyle({
        position: "fixed",
        top,
        left: tagRect.left,
      });
    }
  }, [shouldUsePortal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTag = tagRef.current?.contains(target) ?? false;
      const insideDropdown = dropdownRef.current?.contains(target) ?? false;

      if (!insideTag && !insideDropdown) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [isOpen]);

  const handleTagClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (options.length > 0) {
      setIsOpen((prev) => {
        if (prev) {
          setSearchQuery("");
        }
        return !prev;
      });
    }
    onClick?.(event);
  };

  const handleOptionSelect = (
    option: TagOption,
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    setSelectedOption(option);
    onSelect?.(option, event);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleTagClick(event);
    }
    if (event.key === "Escape") {
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent,
    option: TagOption
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOptionSelect(option, event);
    }
  };

  const hasDropdown = options.length > 0;

  return (
    <>
      <div className={`tag-wrapper ${shouldTruncate ? "tag-wrapper--truncate" : ""} ${className}`} ref={tagRef}>
        <button
          className={`tag ${isOpen ? "tag--open" : ""} ${activeBorder ? "tag--bordered" : ""} ${shouldTruncate ? "tag--truncate" : ""}`}
          style={{
            backgroundColor: activeBg,
            color: activeText,
            ...(activeBorder && activeText ? { borderColor: activeText } : {}),
          }}
          onClick={handleTagClick}
          onKeyDown={handleKeyDown}
          aria-expanded={hasDropdown ? isOpen : undefined}
          aria-haspopup={hasDropdown ? "listbox" : undefined}
          type="button"
        >
          <span className={`tag__label ${shouldTruncate ? "tag__label--truncate" : ""}`} title={shouldTruncate ? activeLabel : undefined}>{activeLabel}</span>
          {hasDropdown && (
            <>
              {showSeparator && (
                <span className="tag__divider" style={{ borderColor: activeText }} />
              )}
              <span className="tag__icon">
                <ChevronDown />
              </span>
            </>
          )}
        </button>

        {hasDropdown && isOpen && !shouldUsePortal && (
          <ul
            className={`tag__dropdown tag__dropdown--${dropDirection}`}
            role="listbox"
            aria-label={label}
            ref={dropdownRef}
          >
            {showSearch && (
              <li className="tag__dropdown-search" role="none">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setIsOpen(false);
                      setSearchQuery("");
                    }
                  }}
                  placeholder="Search..."
                  className="tag__dropdown-search-input"
                  aria-label={`Search ${label}`}
                />
              </li>
            )}
            {filteredOptions.length === 0 ? (
              <li className="tag__dropdown-empty" role="none">
                No results found
              </li>
            ) : (
              filteredOptions.map((option) => (
              <li
                key={option.value}
                className="tag__dropdown-item"
                role="option"
                aria-selected={selectedOption?.value === option.value}
                tabIndex={0}
                onClick={(e) => handleOptionSelect(option, e)}
                onKeyDown={(e) => handleOptionKeyDown(e, option)}
              >
                <span
                  className={`tag tag--option ${option.showBorder ? "tag--bordered" : ""}`}
                  style={{
                    backgroundColor: option.backgroundColor,
                    color: option.textColor,
                    ...(option.showBorder && option.textColor ? { borderColor: option.textColor } : {}),
                  }}
                >
                  <span className="tag__label">{option.label}</span>
                </span>
              </li>
              ))
            )}
          </ul>
        )}
      </div>

      {hasDropdown &&
        isOpen &&
        shouldUsePortal &&
        createPortal(
          <ul
            className="tag__dropdown tag__dropdown--portal"
            role="listbox"
            aria-label={label}
            ref={dropdownRef}
            style={portalStyle}
          >
            {showSearch && (
              <li className="tag__dropdown-search" role="none">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setIsOpen(false);
                      setSearchQuery("");
                    }
                  }}
                  placeholder="Search..."
                  className="tag__dropdown-search-input"
                  aria-label={`Search ${label}`}
                />
              </li>
            )}
            {filteredOptions.length === 0 ? (
              <li className="tag__dropdown-empty" role="none">
                No results found
              </li>
            ) : (
              filteredOptions.map((option) => (
              <li
                key={option.value}
                className="tag__dropdown-item"
                role="option"
                aria-selected={selectedOption?.value === option.value}
                tabIndex={0}
                onClick={(e) => handleOptionSelect(option, e)}
                onKeyDown={(e) => handleOptionKeyDown(e, option)}
              >
                <span
                  className={`tag tag--option ${option.showBorder ? "tag--bordered" : ""}`}
                  style={{
                    backgroundColor: option.backgroundColor,
                    color: option.textColor,
                    ...(option.showBorder && option.textColor ? { borderColor: option.textColor } : {}),
                  }}
                >
                  <span className="tag__label">{option.label}</span>
                </span>
              </li>
              ))
            )}
          </ul>,
          document.body
        )}
    </>
  );
};
